import React, { type CSSProperties, type ReactNode } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { type LuxPlusRenewalBonusStep } from 'luxPlus/hooks/useLuxPlusRenewalBonus'
import { type LuxPlusRenewalStepState } from 'loyalty/lib/luxPlusRenewalActions'

/**
 * The staggered 1st/2nd/3rd renewal ladder, shared by the landing-page banner and the
 * "More years, more rewards" modal. The default export renders a horizontal stepper on tablet+
 * and a vertical stepper on mobile (the ladder only). This module is also the shared home for the
 * renewal-bonus message descriptors ({@link renewalBonusMessages}) and the surrounding card chrome
 * ({@link RenewalBonusInnerCard} / {@link RenewalBonusLadderBox} / {@link RenewalBonusGiftIcon})
 * reused across the banner, modal and retention surfaces.
 */
export const renewalBonusMessages = defineMessages({
  stepLabel: {
    id: 'Vf6p7hv_Lvh3O9T7N-i4',
    defaultMessage: '{isFinal, select, true {{year, select, 1 {1st Renewal +} 2 {2nd Renewal +} 3 {3rd Renewal +} other {{year}th Renewal +}}} other {{year, select, 1 {1st Renewal} 2 {2nd Renewal} 3 {3rd Renewal} other {{year}th Renewal}}}}',
    description: 'Timeline step label, e.g. "1st Renewal", "3rd Renewal +", on the renewal bonus ladder',
  },
  points: {
    id: 'aRYxRxgHx43H3upuC8Bv',
    defaultMessage: '{points} Société Points',
    description: 'Points earned line for a renewal bonus timeline step, e.g. "7,500 Société Points"',
  },
  statusCredits: {
    id: 'RPEvuGKwzuUL0M7bD23f',
    defaultMessage: '+ {statusCredits} Status Credits',
    description: 'Status credits earned line for a renewal bonus timeline step, e.g. "+ 100 Status Credits"',
  },
  footnote: {
    id: 'MgA7AbC8XeLWNIPXJzp9',
    defaultMessage: '*Renewal bonuses are available to Société members and are based on consecutive annual {membershipName} renewals. Renewal progression resets if your membership lapses.',
    description: 'Footnote under the LuxPlus annual renewal bonus ladder',
  },
  noProgressionHeading: {
    id: 'JMkjs92TncwFOQJzjGsl',
    defaultMessage: 'Receive {rewardAmount} at your next annual renewal',
    description: 'Headline shown when the renewal reward has no tier progression to display. {rewardAmount} is the formatted reward, e.g. "7,500 Société Points + 100 Status Credits".',
  },
  currentStepUnlock: {
    id: 'Yt1NP6WrVVVhEozfPWM2',
    defaultMessage: 'Unlock this bonus',
    description: 'Caption under the current renewal step when the member is in the payment-recovery/grace window',
  },
  currentStepAlmostHere: {
    id: 'cha860ZPFpgTjC4KDKgH',
    defaultMessage: "You're almost here",
    description: 'Caption under the member current renewal step on the renewal bonus ladder',
  },
  moreYearsMoreRewards: {
    id: 'WICxNSl0XMUFDL6RdMcF',
    defaultMessage: 'More years, more rewards',
    description: 'Title for the staggered LuxPlus+ renewal reward ladder (banner tile, modal and benefits list)',
  },
  nextTierBlurb: {
    id: '_Pr7fu6xFXICyuSZCN_d',
    defaultMessage: 'Bringing you closer to {nextTier} status, unlocking free upgrades, airport lounge passes & more.',
    description: 'Describes the benefit of reaching the next loyalty tier',
  },
})

/* ---------- Shared renewal-bonus card chrome ---------- */

// Outer bordered card wrapping a renewal-bonus block, shared by the ladder modal and the
// retention screen.
export const RenewalBonusInnerCard = styled.div`
  border: 1px solid ${(props) => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${(props) => props.theme.borderRadius.L};
  padding: ${rem(20)};
`

// Nested box holding the renewal ladder itself.
export const RenewalBonusLadderBox = styled.div`
  background-color: ${(props) => props.theme.palette.neutral.surface.lightBackground};
  border-radius: ${(props) => props.theme.borderRadius.L};
  padding: ${rem(20)};
`

// Gift icon in the LuxPlus+ blue, used in the eyebrow row above the ladder.
export const RenewalBonusGiftIcon = styled(LineGiftIcon)`
  color: ${(props) => props.theme.palette.product.luxPlus.foreground};
`

/**
 * The collapsed headline shown when a member has no ladder progression to display, e.g.
 * "Receive 7,500 Société Points + 100 Status Credits at your next annual renewal".
 */
export function useNoProgressionHeading() {
  const intl = useIntl()
  const formatRewardAmount = useRenewalRewardAmountText()
  return (
    step: LuxPlusRenewalBonusStep | undefined,
    { hideStatusCredits = false }: { hideStatusCredits?: boolean } = {},
  ): string => intl.formatMessage(renewalBonusMessages.noProgressionHeading, {
    rewardAmount: formatRewardAmount(step, { hideStatusCredits }),
  })
}

const StepChip = styled.span<{ $state: LuxPlusRenewalStepState }>`
  flex-shrink: 0;
  border-radius: ${(props) => props.theme.borderRadius.round};
  padding: ${rem(4)} ${rem(12)};
  font-size: ${rem(12)};
  font-weight: 600;
  line-height: ${rem(16)};
  white-space: nowrap;
  ${(props) => {
    const { product, neutral } = props.theme.palette
    switch (props.$state) {
      // Next reward: solid blue with white text.
      case 'current':
        return `
          background-color: ${product.luxPlus.background};
          color: ${product.luxPlus.contrast};
          border: 1px solid ${product.luxPlus.background};
        `
      // Already earned: white with blue border + blue text.
      case 'completed':
        return `
          background-color: ${neutral.surface.normalBackground};
          color: ${product.luxPlus.foreground};
          border: 1px solid ${product.luxPlus.foreground};
        `
      // Not yet reached: white with grey border + black text.
      default:
        return `
          background-color: ${neutral.surface.normalBackground};
          color: ${neutral.surface.normalContrast};
          border: 1px solid ${neutral.surface.lightForeground};
        `
    }
  }}
`

/* ---------- Desktop: horizontal stepper (pills in a row, amounts below) ---------- */

// Rendered tablet-and-up. CSSBreakpoint (dynamic) hides it with CSS pre-mount, then drops it from
// the DOM post-mount so assistive tech doesn't read both the desktop and mobile steppers. Width cap
// is passed as a CSS custom property rather than a styled number prop.
const DesktopTimeline = styled(CSSBreakpoint)`
  display: flex;
  flex-direction: row;

  ${mediaQueryUp.desktop} {
    flex: 1;
    max-width: var(--timeline-max-width, none);
  }
`

const DesktopStep = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${rem(8)};
`

// Optionally caps the amount/caption width so it wraps under the pill (the pill + connector row
// above still spans the full step width, keeping the connecting line continuous).
const DesktopAmounts = styled.div`
  width: 100%;
  max-width: var(--amounts-max-width, none);
`

const PillRow = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;
  gap: ${rem(4)};
`

const HalfConnector = styled.span<{ $blue: boolean; $hidden: boolean }>`
  flex: 1;
  height: ${rem(2)};
  visibility: ${(props) => (props.$hidden ? 'hidden' : 'visible')};
  background-color: ${(props) => (props.$blue
    ? props.theme.palette.product.luxPlus.foreground
    : props.theme.palette.neutral.surface.lightForeground)};
`

/* ---------- Mobile: vertical stepper (pill left + amounts right) ---------- */

// Rendered mobile-only; the desktop stepper (above) covers tablet-and-up. See DesktopTimeline for
// why this uses CSSBreakpoint dynamic rather than a CSS-only `display: none`.
const MobileTimeline = styled(CSSBreakpoint)`
  display: flex;
  flex-direction: column;
`

const MobileStep = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${rem(12)};
`

const MobilePillCol = styled.div`
  flex-shrink: 0;
  width: ${rem(104)};
  display: flex;
  flex-direction: column;
  align-items: center;
`

const MobileAmounts = styled.div`
  flex: 1;
  min-width: 0;
  padding-bottom: ${rem(16)};
`

const VConnector = styled.span<{ $blue: boolean }>`
  flex: 1;
  width: ${rem(1)};
  min-height: ${rem(12)};
  margin-block: ${rem(4)};
  background-color: ${(props) => (props.$blue
    ? props.theme.palette.product.luxPlus.foreground
    : props.theme.palette.neutral.surface.lightForeground)};
`

type StepAlign = 'center' | 'start'

export function useStepLabel(step: LuxPlusRenewalBonusStep, isFinal: boolean) {
  const intl = useIntl()
  return intl.formatMessage(renewalBonusMessages.stepLabel, { year: step.year, isFinal })
}

/**
 * Formats a step's reward as a single string, e.g. "7,500 Société Points + 100 Status Credits",
 * used for the headline copy that summarises a member's next reward.
 */
export function useRenewalRewardAmountText() {
  const intl = useIntl()
  return (
    step: LuxPlusRenewalBonusStep | undefined,
    { hideStatusCredits = false }: { hideStatusCredits?: boolean } = {},
  ): string => [
    step?.points !== undefined
    && intl.formatMessage(renewalBonusMessages.points, { points: intl.formatNumber(step.points) }),
    step?.statusCredits !== undefined && !hideStatusCredits
    && intl.formatMessage(renewalBonusMessages.statusCredits, { statusCredits: intl.formatNumber(step.statusCredits) }),
  ].filter(Boolean).join(' ')
}

/** Points / status-credit lines + an optional caption under the current step, shared by both layouts. */
function StepAmounts({ step, align, captionFirst = false, currentStepCaption }: {
  step: LuxPlusRenewalBonusStep
  align: StepAlign
  captionFirst?: boolean
  currentStepCaption?: ReactNode
}) {
  const intl = useIntl()
  const isCurrent = step.state === 'current'
  // Only the member's next (current) reward is emphasised in loyalty blue + bold.
  const amountColour = isCurrent ? 'lux-plus' : 'normal-contrast'
  const amountWeight = isCurrent ? 'bold' : 'normal'
  const hasAmounts = step.points !== undefined || step.statusCredits !== undefined

  const caption = isCurrent && currentStepCaption
    ? (
        <CaptionTextBlock variant="medium" weight="bold" colour="success-dark" align={align}>
          {currentStepCaption}
        </CaptionTextBlock>
      )
    : null

  return (
    <Group direction="vertical" gap={0} horizontalAlign={align}>
      {captionFirst && caption}
      {hasAmounts && (
        <Group direction="vertical" gap={0} horizontalAlign={align}>
          {step.points !== undefined && (
            <BodyTextBlock variant="medium" weight={amountWeight} colour={amountColour} align={align}>
              {intl.formatMessage(renewalBonusMessages.points, { points: intl.formatNumber(step.points) })}
            </BodyTextBlock>
          )}
          {step.statusCredits !== undefined && (
            <BodyTextBlock variant="medium" weight={amountWeight} colour={amountColour} align={align}>
              {intl.formatMessage(renewalBonusMessages.statusCredits, { statusCredits: intl.formatNumber(step.statusCredits) })}
            </BodyTextBlock>
          )}
        </Group>
      )}
      {!captionFirst && caption}
    </Group>
  )
}

interface RenewalStepProps {
  step: LuxPlusRenewalBonusStep
  isFirst: boolean
  isFinal: boolean
  prevCompleted: boolean
  currentStepCaption?: ReactNode
  amountsMaxWidthRem?: number
}

function DesktopRenewalStep({ step, isFirst, isFinal, prevCompleted, currentStepCaption, amountsMaxWidthRem }: RenewalStepProps) {
  const label = useStepLabel(step, isFinal)
  return (
    <DesktopStep>
      <PillRow>
        {/* The connector into a step is blue once the previous step is completed. */}
        <HalfConnector $hidden={isFirst} $blue={prevCompleted} />
        <StepChip $state={step.state}>{label}</StepChip>
        <HalfConnector $hidden={isFinal} $blue={step.state === 'completed'} />
      </PillRow>
      <DesktopAmounts style={{ '--amounts-max-width': amountsMaxWidthRem ? rem(amountsMaxWidthRem) : undefined } as CSSProperties}>
        <StepAmounts step={step} align="center" currentStepCaption={currentStepCaption} />
      </DesktopAmounts>
    </DesktopStep>
  )
}

function MobileRenewalStep({ step, isFinal, currentStepCaption }: Pick<RenewalStepProps, 'step' | 'isFinal' | 'currentStepCaption'>) {
  const label = useStepLabel(step, isFinal)
  return (
    <MobileStep>
      <MobilePillCol>
        <StepChip $state={step.state}>{label}</StepChip>
        {/* Vertical connector down to the next pill; blue once this step is completed. */}
        {!isFinal && <VConnector $blue={step.state === 'completed'} />}
      </MobilePillCol>
      <MobileAmounts>
        <StepAmounts step={step} align="start" captionFirst currentStepCaption={currentStepCaption} />
      </MobileAmounts>
    </MobileStep>
  )
}

/* ---------- Loading skeletons (mirror the stepper structure) ---------- */

const SKELETON_STEPS = [0, 1, 2]

/** Shimmer lines standing in for a heading / eyebrow while amounts are being fetched. */
export function RenewalTextSkeleton({ lineWidths, height = 20 }: { lineWidths: Array<number>; height?: number }) {
  return (
    <Group direction="vertical" gap={8}>
      {lineWidths.map((width, index) => (
        <LoadingBox key={index} width={width} height={height} borderRadius="S" />
      ))}
    </Group>
  )
}

function DesktopSkeletonStep({ isFirst, isFinal, amountsMaxWidthRem }: { isFirst: boolean; isFinal: boolean; amountsMaxWidthRem?: number }) {
  return (
    <DesktopStep>
      <PillRow>
        <HalfConnector $hidden={isFirst} $blue={false} />
        <LoadingBox width={92} height={24} borderRadius="round" />
        <HalfConnector $hidden={isFinal} $blue={false} />
      </PillRow>
      <DesktopAmounts style={{ '--amounts-max-width': amountsMaxWidthRem ? rem(amountsMaxWidthRem) : undefined } as CSSProperties}>
        <Group direction="vertical" gap={4} horizontalAlign="center">
          <LoadingBox width={110} height={16} borderRadius="S" />
          <LoadingBox width={84} height={16} borderRadius="S" />
        </Group>
      </DesktopAmounts>
    </DesktopStep>
  )
}

function MobileSkeletonStep({ isFinal }: { isFinal: boolean }) {
  return (
    <MobileStep>
      <MobilePillCol>
        <LoadingBox width={92} height={24} borderRadius="round" />
        {!isFinal && <VConnector $blue={false} />}
      </MobilePillCol>
      <MobileAmounts>
        <Group direction="vertical" gap={4}>
          <LoadingBox width={110} height={16} borderRadius="S" />
          <LoadingBox width={84} height={16} borderRadius="S" />
        </Group>
      </MobileAmounts>
    </MobileStep>
  )
}

interface Props {
  steps: Array<LuxPlusRenewalBonusStep>
  /** Caption shown under the current step (e.g. "You're almost here" / "Your first bonus"). */
  currentStepCaption?: ReactNode
  /** Caps the desktop stepper width when it sits beside other content (e.g. the banner heading). */
  desktopMaxWidthRem?: number
  /** Caps each desktop step's amount/caption width so it wraps under the pill. */
  desktopAmountsMaxWidthRem?: number
  /** Renders shimmer placeholders in place of the real steps while amounts are being fetched. */
  isLoading?: boolean
}

function LuxPlusRenewalBonusTimeline({ steps, currentStepCaption, desktopMaxWidthRem, desktopAmountsMaxWidthRem, isLoading }: Props) {
  if (isLoading) {
    return (
      <>
        <DesktopTimeline min="tablet" dynamic style={{ '--timeline-max-width': desktopMaxWidthRem ? rem(desktopMaxWidthRem) : undefined } as CSSProperties}>
          {SKELETON_STEPS.map((index) => (
            <DesktopSkeletonStep
              key={index}
              isFirst={index === 0}
              isFinal={index === SKELETON_STEPS.length - 1}
              amountsMaxWidthRem={desktopAmountsMaxWidthRem}
            />
          ))}
        </DesktopTimeline>
        <MobileTimeline max="mobile" dynamic>
          {SKELETON_STEPS.map((index) => (
            <MobileSkeletonStep key={index} isFinal={index === SKELETON_STEPS.length - 1} />
          ))}
        </MobileTimeline>
      </>
    )
  }

  return (
    <>
      <DesktopTimeline min="tablet" dynamic style={{ '--timeline-max-width': desktopMaxWidthRem ? rem(desktopMaxWidthRem) : undefined } as CSSProperties}>
        {steps.map((step, index) => (
          <DesktopRenewalStep
            key={step.year}
            step={step}
            isFirst={index === 0}
            isFinal={index === steps.length - 1}
            prevCompleted={index > 0 && steps[index - 1].state === 'completed'}
            currentStepCaption={currentStepCaption}
            amountsMaxWidthRem={desktopAmountsMaxWidthRem}
          />
        ))}
      </DesktopTimeline>
      <MobileTimeline max="mobile" dynamic>
        {steps.map((step, index) => (
          <MobileRenewalStep
            key={step.year}
            step={step}
            isFinal={index === steps.length - 1}
            currentStepCaption={currentStepCaption}
          />
        ))}
      </MobileTimeline>
    </>
  )
}

export default LuxPlusRenewalBonusTimeline
